import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CabiMascot from "../../../components/Kids/CabiMascot";

const FF = "'Fredoka',sans-serif";
const API = process.env.REACT_APP_API_URL;

const SUBJECT_COLOR = {
  english: "#2FA15A", reading: "#2FA15A", math: "#EE5F2B",
  science: "#13B6C9", coding: "#7A4FD0", art: "#E0518A", arabic: "#C8491E", general: "#EE5F2B",
};

const SUBJECT_EMOJI = {
  english: "📚", reading: "📖", math: "🔢", science: "🔬",
  coding: "💻", art: "🎨", arabic: "🕌", general: "⭐",
};

const BUILDING_LABELS = [
  "Library", "Academy", "Lab", "Studio", "Tower", "Arena", "Castle",
];

export default function LearningWorldMap({
  childName = "Friend", coins = 0, xp = 0, level = 1, streak = 0, grade = 1,
  journey = [], currentIdx = 0, onPlay, navigate, childId, onGrid, aiEnabled = true,
}) {
  const { t } = useTranslation();
  const SLOT_COUNT = 7;
  const stops = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const game = journey[i];
    let status;
    if (!game)               status = "empty";
    else if (i < currentIdx) status = "done";
    else if (i === currentIdx) status = "current";
    else                     status = "available";
    return { game, status, isLast: i === SLOT_COUNT - 1 };
  });

  const firstSelectable = Math.min(currentIdx, stops.length - 1);
  const [selected, setSelected] = useState(firstSelectable);
  const sel = stops[selected] || stops[0];
  const selGame = sel.game;
  const locked = sel.status === "empty";
  const isDone = sel.status === "done";
  const subjColor = SUBJECT_COLOR[(selGame?.subject || "general").toLowerCase()] || "#EE5F2B";

  const xpBar = ((xp % 100) / 100) * 100;
  const gradeLabel = grade === 0 ? "K" : `Grade ${grade}`;

  const playLabel = locked
    ? t("kids.games.map.comingSoon")
    : isDone ? t("kids.games.map.playAgain") : t("kids.games.map.play");
  const handlePlay = () => { if (!locked && selGame && onPlay) onPlay(selGame); };

  // Detect orientation for layout
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
  useEffect(() => {
    const onResize = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch active map theme + all available themes for switcher
  const [themeKey, setThemeKey] = useState("town");
  const [themeConfig, setThemeConfig] = useState(null);
  const [allThemes, setAllThemes] = useState([]);
  const [showThemePicker, setShowThemePicker] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/kids/map-theme`).then(r => {
      const data = r.data?.data;
      if (data?.theme_key) setThemeKey(data.theme_key);
      if (data?.config) setThemeConfig(data.config);
    }).catch(() => {});
    axios.get(`${API}/api/kids/map-themes`).then(r => {
      setAllThemes(r.data?.data || []);
    }).catch(() => {});
  }, []);

  return (
    <div className="fixed inset-0 font-body overflow-hidden">

      <div className="absolute inset-0" style={{ bottom: "calc(78px + env(safe-area-inset-bottom, 0px) + 68px)" }}>
        <MapScene themeKey={themeKey} config={themeConfig} stops={stops} currentIdx={currentIdx}
          selected={selected} setSelected={setSelected} isPortrait={isPortrait} mascotAt={selected} />
      </div>

      {/* ── Left-side HUD (vertical strip) ── */}
      <div className="absolute inset-y-0 start-0 z-[60] flex flex-col gap-1.5 pb-2.5 ps-2 pointer-events-none"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 10px)" }}>
        {/* Avatar + name */}
        <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-card shadow-card py-1 pe-2.5 ps-[5px] pointer-events-auto">
          <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center border-2 border-brand font-display font-bold text-[13px] text-brand-deep"
            style={{ background: "radial-gradient(circle at 50% 38%, #FFD9BF, #FF8A4C)" }}>
            {childName.charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-[11px] text-ink">{childName}</div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className="font-extrabold text-[9px] text-brand">Lv {level}</span>
              <span className="inline-block w-9 h-[5px] rounded-full bg-cardline overflow-hidden">
                <span className="block h-full rounded-full bg-gradient-to-r from-brand-light to-brand" style={{ width: `${xpBar}%` }} />
              </span>
            </div>
          </div>
        </div>
        {/* Coins */}
        <div className="self-start flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-[10px] px-2 py-[3px] shadow-card font-extrabold text-[10px] text-ink pointer-events-auto">🪙 {coins}</div>
        {/* Streak */}
        <div className="self-start flex items-center gap-1 bg-brand/10 border border-brand-light/60 rounded-[10px] px-2 py-[2px] font-extrabold text-[10px] text-brand-deep pointer-events-auto">🔥 {streak}</div>
        {/* Grade */}
        <div className="self-start bg-white/85 rounded-[10px] px-2 py-[2px] font-extrabold text-[9px] text-muted pointer-events-auto">{gradeLabel}</div>
        {/* Theme switcher */}
        {allThemes.length > 1 && (
          <button onClick={() => setShowThemePicker(p => !p)}
            className="self-start mt-1 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-[10px] px-2 py-[3px] shadow-card font-extrabold text-[10px] text-aiaccent border-0 cursor-pointer pointer-events-auto">
            🎨 Theme
          </button>
        )}
      </div>

      {/* ── Theme Picker Overlay ── */}
      {showThemePicker && (
        <div className="absolute inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShowThemePicker(false)}>
          <div className="bg-white rounded-sheet p-5 max-w-[400px] w-full max-h-[80vh] overflow-y-auto shadow-sheet"
            onClick={e => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-ink mb-3 text-center">🎨 Choose Your World</h3>
            <div className="grid grid-cols-2 gap-2.5">
              {allThemes.map(t => {
                const THEME_ICONS = { town: "🏘️", island: "🏝️", space: "🚀", jungle: "🌴", underwater: "🐠", candy: "🍭", arctic: "🧊" };
                const isActive = t.theme_key === themeKey;
                return (
                  <button key={t.id} onClick={() => { setThemeKey(t.theme_key); setThemeConfig(t.config || null); setShowThemePicker(false); }}
                    className={
                      "rounded-card p-3.5 text-center cursor-pointer transition-all border-2 " +
                      (isActive ? "bg-brand/10 border-brand" : "bg-white border-cardline hover:border-brand")
                    }>
                    <div className="text-[28px] mb-1">{THEME_ICONS[t.theme_key] || "🎮"}</div>
                    <div className="font-display font-bold text-[13px] text-ink">{t.name}</div>
                    {t.description && <div className="text-[10px] text-muted mt-0.5 leading-snug">{t.description.substring(0, 60)}</div>}
                    {isActive && <div className="text-[9px] font-extrabold text-brand mt-1">✓ ACTIVE</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Ultra-compact bottom panel ── */}
      {/* Positioned above KidsShell's persistent Home/Watch/Progress tab bar
          (fixed, ~78px + safe-area tall, z-40) rather than at true viewport
          bottom — this map view always renders inside that shell, so
          anchoring to bottom-0 here would sit right behind it. */}
      <div className="absolute inset-x-0 z-[55] bg-white/95 backdrop-blur-md rounded-2xl shadow-sheet mx-2"
        style={{ bottom: "calc(78px + env(safe-area-inset-bottom, 0px) + 8px)" }}>
        <div className="max-w-[600px] mx-auto px-2.5 pt-1.5 pb-1">
          <div className="flex items-center gap-1.5">
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
              <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[8px] font-extrabold uppercase tracking-wide text-white" style={{ background: subjColor }}>
                {selGame?.subject || "—"}
              </span>
              <div className="font-display font-semibold text-[13px] text-ink whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
                {selGame?.name || (sel.isLast ? "Castle" : "—")}
              </div>
            </div>
            <button onClick={handlePlay} disabled={locked || !selGame}
              className={
                "shrink-0 rounded-lg px-3.5 py-1.5 font-display font-bold text-xs border-0 " +
                (locked || !selGame
                  ? "bg-cream text-faint cursor-not-allowed"
                  : isDone
                    ? "text-white bg-success shadow-card cursor-pointer"
                    : "text-white bg-gradient-to-b from-brand-light to-brand shadow-primary cursor-pointer")
              }>
              {locked ? "🔒" : isDone ? `↻ ${playLabel}` : `▶ ${playLabel}`}
            </button>
            <div className="flex gap-0.5 shrink-0">
              {aiEnabled && <NavBtn icon="🤖" onClick={() => navigate(`/kids/ai-chat?childId=${childId}`)} />}
              <NavBtn icon="🔮" onClick={() => navigate(`/kids/ar?childId=${childId}`)} />
              <NavBtn icon="📺" onClick={() => navigate(`/kids/watch?childId=${childId}`)} />
              {onGrid && <NavBtn icon="▦" onClick={onGrid} />}
              <NavBtn icon="👨‍👩‍👧" onClick={() => navigate("/kids/parent/dashboard")} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes lwBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes lwBalloon{0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-14px) rotate(2deg)}}
        @keyframes lwFlag{0%,100%{transform:skewX(0deg)}50%{transform:skewX(-4deg)}}
        @keyframes lwSparkle{0%,100%{opacity:0.2;transform:scale(0.7)}50%{opacity:0.9;transform:scale(1.1)}}
        @keyframes lwStar{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
        @keyframes lwCarR{from{transform:translateX(-60px)}to{transform:translateX(900px)}}
        @keyframes lwCarL{from{transform:translateX(900px)}to{transform:translateX(-60px)}}
      `}</style>
    </div>
  );
}

/* ── Scene switcher: renders the correct theme ── */
function MapScene({ themeKey, config, ...props }) {
  if (themeKey === "island")     return <IslandScene config={config} {...props} />;
  if (themeKey === "space")      return <SpaceScene config={config} {...props} />;
  if (themeKey === "jungle")     return <JungleScene config={config} {...props} />;
  if (themeKey === "underwater") return <UnderwaterScene config={config} {...props} />;
  if (themeKey === "candy")      return <CandyScene config={config} {...props} />;
  if (themeKey === "arctic")     return <ArcticScene config={config} {...props} />;
  return <TownScene config={config} {...props} />;
}

/* ── Full-screen cartoon town — portrait & landscape layouts ── */
function TownScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const { t } = useTranslation();
  const VB = isPortrait
    ? { w: 600, h: 750, ar: "xMidYMin meet" }
    : { w: 1000, h: 520, ar: "xMidYMin meet" };

  const BUILDINGS = isPortrait ? [
    { x: 100, y: 600, w: 40, h: 44 },
    { x: 390, y: 530, w: 42, h: 46 },
    { x: 120, y: 460, w: 42, h: 46 },
    { x: 400, y: 390, w: 40, h: 44 },
    { x: 110, y: 320, w: 42, h: 46 },
    { x: 390, y: 250, w: 42, h: 46 },
    { x: 250, y: 170, w: 46, h: 50 },
  ] : [
    { x: 80,  y: 410, w: 42, h: 46 },
    { x: 280, y: 370, w: 44, h: 48 },
    { x: 500, y: 330, w: 44, h: 48 },
    { x: 760, y: 290, w: 42, h: 46 },
    { x: 600, y: 225, w: 42, h: 46 },
    { x: 360, y: 165, w: 44, h: 48 },
    { x: 645, y: 100, w: 48, h: 52 },
  ];

  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");

  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const statusOpacity = (s) => s.status === "empty" ? 0.5 : 1;

  // Ground & sky Y positions scale with viewBox
  const groundY = VB.h * 0.55;
  const skyH = VB.h;

  const tc = mergeDeep(SCENE_DEFAULTS.town, config);
  const colorOf = (i) => {
    const override = tc.buildings?.[(i + 1).toString()]?.color;
    return override || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B";
  };
  const signpostLabel = tc.signpost.label === "Cabil Town" ? t("kids.games.map.cabilTown") : tc.signpost.label;
  const signpostSublabel = tc.signpost.sublabel === "Learn!" ? t("kids.games.map.learn") : tc.signpost.sublabel;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs>
        <linearGradient id="twSky" x1="0" y1="0" x2="0" y2="1">
          {tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
        </linearGradient>
        <radialGradient id="castleGlow">
          <stop offset="0" stopColor="#FFE08A" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FFE08A" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ── Sky fills entire viewBox ── */}
      <rect width={VB.w} height={VB.h} fill="url(#twSky)" />

      {/* ── Distant mountains ── */}
      {tc.decorations.mountains?.visible !== false && <>
      <polygon points={`0,${groundY - 60} ${VB.w * 0.12},${groundY - 160} ${VB.w * 0.25},${groundY - 100} ${VB.w * 0.38},${groundY - 180} ${VB.w * 0.5},${groundY - 90} ${VB.w * 0.5},${groundY - 60}`}
        fill="#8FBFA8" opacity=".45" />
      <polygon points={`${VB.w * 0.45},${groundY - 60} ${VB.w * 0.58},${groundY - 170} ${VB.w * 0.7},${groundY - 100} ${VB.w * 0.82},${groundY - 200} ${VB.w * 0.92},${groundY - 120} ${VB.w},${groundY - 60}`}
        fill="#7AAF96" opacity=".4" />
      {/* Snow caps */}
      <polygon points={`${VB.w * 0.38},${groundY - 180} ${VB.w * 0.36},${groundY - 145} ${VB.w * 0.40},${groundY - 145}`} fill="#EAF3EC" opacity=".65" />
      <polygon points={`${VB.w * 0.82},${groundY - 200} ${VB.w * 0.80},${groundY - 160} ${VB.w * 0.84},${groundY - 160}`} fill="#EAF3EC" opacity=".6" />
      </>}

      {/* ── Skyline silhouettes ── */}
      <rect x={VB.w * 0.15} y={groundY - 100} width={VB.w * 0.03} height={VB.h * 0.08} rx="2" fill="#B0BFC6" opacity=".3" />
      <rect x={VB.w * 0.19} y={groundY - 80} width={VB.w * 0.025} height={VB.h * 0.065} rx="2" fill="#A0B0B8" opacity=".25" />
      <rect x={VB.w * 0.7} y={groundY - 110} width={VB.w * 0.03} height={VB.h * 0.09} rx="2" fill="#B0BFC6" opacity=".3" />
      <rect x={VB.w * 0.74} y={groundY - 85} width={VB.w * 0.022} height={VB.h * 0.07} rx="2" fill="#A0B0B8" opacity=".25" />

      {/* ── Clouds ── */}
      {tc.decorations.clouds?.visible !== false && <>
      <Cloud cx={VB.w * 0.15} cy={VB.h * 0.08} rx={VB.w * 0.06} />
      <Cloud cx={VB.w * 0.5}  cy={VB.h * 0.06} rx={VB.w * 0.07} />
      <Cloud cx={VB.w * 0.82} cy={VB.h * 0.1}  rx={VB.w * 0.05} />
      </>}

      {/* ── Sun ── */}
      {tc.decorations.sun?.visible !== false && <>
      <circle cx={VB.w * 0.9} cy={VB.h * 0.1} r={VB.w * 0.035} fill="#FFE066" opacity=".9" />
      <circle cx={VB.w * 0.9} cy={VB.h * 0.1} r={VB.w * 0.028} fill="#FFD700" />
      </>}

      {/* ── Green ground ── */}
      <rect x="0" y={groundY} width={VB.w} height={VB.h - groundY} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.3} cy={groundY} rx={VB.w * 0.35} ry={VB.h * 0.06} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.75} cy={groundY} rx={VB.w * 0.3} ry={VB.h * 0.055} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.1} cy={groundY + 30} rx={VB.w * 0.2} ry={VB.h * 0.04} fill={tc.ground.accent} />
      <ellipse cx={VB.w * 0.9} cy={groundY + 25} rx={VB.w * 0.18} ry={VB.h * 0.035} fill={tc.ground.accent} />

      {/* ── Cross road ── */}
      <line x1="0" y1={groundY + 45} x2={VB.w} y2={groundY + 45} stroke="#D0C8A0" strokeWidth="20" />
      <line x1="0" y1={groundY + 45} x2={VB.w} y2={groundY + 45} stroke="#BBB490" strokeWidth="2" strokeDasharray="14 22" />

      {/* ── Animated cars ── */}
      {tc.decorations.cars?.visible !== false && <>
      <g style={{ animation: "lwCarR 14s linear infinite" }}>
        <rect x="0" y={groundY + 37} width="34" height="15" rx="6" fill="#FF6B6B" />
        <rect x="3" y={groundY + 39} width="8" height="6" rx="1.5" fill="#87CEEB" opacity=".7" />
        <rect x="22" y={groundY + 39} width="8" height="6" rx="1.5" fill="#87CEEB" opacity=".7" />
        <circle cx="8" cy={groundY + 54} r="3" fill="#333" /><circle cx="26" cy={groundY + 54} r="3" fill="#333" />
      </g>
      <g style={{ animation: "lwCarL 18s linear infinite" }}>
        <rect x="0" y={groundY + 33} width="30" height="13" rx="5" fill="#5BBCE6" />
        <circle cx="7" cy={groundY + 48} r="2.5" fill="#333" /><circle cx="23" cy={groundY + 48} r="2.5" fill="#333" />
      </g>
      </>}

      {/* ── Main winding road ── */}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" opacity=".5" />
      {/* Lit-up progress road (orange glow up to current stop) */}
      {currentIdx > 0 && (() => {
        const litPoints = BUILDINGS.slice(0, currentIdx + 1).map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
        return <>
          <polyline points={litPoints} fill="none" stroke="#FF8A4A" strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={litPoints} fill="none" stroke="#FFD700" strokeWidth={tc.road.width - 12} strokeLinejoin="round" strokeLinecap="round" opacity=".3" />
        </>;
      })()}
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />

      {/* ── Scattered trees ── */}
      {tc.decorations.trees?.visible !== false &&
        scatterTrees(VB, isPortrait).map((t, i) => <Tree key={i} x={t.x} y={t.y} s={t.s} />)}

      {/* ── Pond (right side) ── */}
      {tc.decorations.pond?.visible !== false && <>
      <ellipse cx={VB.w * 0.88} cy={groundY + 12} rx={VB.w * 0.06} ry={VB.h * 0.025} fill="#4BAED4" />
      <ellipse cx={VB.w * 0.88} cy={groundY + 10} rx={VB.w * 0.06} ry={VB.h * 0.025} fill="#5BBCE6" />
      <ellipse cx={VB.w * 0.87} cy={groundY + 7} rx={VB.w * 0.025} ry={VB.h * 0.009} fill="#9BE0F5" opacity=".6" />
      <text x={VB.w * 0.87} y={groundY + 14} fontSize="10" style={{ userSelect: "none" }}>🦆</text>
      <text x={VB.w * 0.895} y={groundY + 11} fontSize="8" style={{ userSelect: "none" }}>🦆</text>
      </>}

      {/* ── Flower patches (spread across) ── */}
      {tc.decorations.flowers?.visible !== false && <>
      <circle cx={VB.w * 0.18} cy={groundY + 18} r="3" fill="#FF6B8A" /><circle cx={VB.w * 0.18} cy={groundY + 18} r="1.5" fill="#FFE066" />
      <circle cx={VB.w * 0.2} cy={groundY + 22} r="2.5" fill="#E0518A" /><circle cx={VB.w * 0.2} cy={groundY + 22} r="1.2" fill="#FFE066" />
      <circle cx={VB.w * 0.5} cy={groundY + 15} r="3" fill="#B366E0" /><circle cx={VB.w * 0.5} cy={groundY + 15} r="1.5" fill="#fff" />
      <circle cx={VB.w * 0.52} cy={groundY + 20} r="2.5" fill="#FF8A8A" /><circle cx={VB.w * 0.52} cy={groundY + 20} r="1.2" fill="#FFE066" />
      <circle cx={VB.w * 0.78} cy={groundY + 16} r="3" fill="#FF6B8A" /><circle cx={VB.w * 0.78} cy={groundY + 16} r="1.5" fill="#fff" />
      <circle cx={VB.w * 0.8} cy={groundY + 22} r="2.5" fill="#5BBCE6" /><circle cx={VB.w * 0.8} cy={groundY + 22} r="1.2" fill="#fff" />
      </>}

      {/* ── Playground (center-right) ── */}
      {tc.decorations.playground?.visible !== false && <>
      <rect x={VB.w * 0.72} y={groundY + 3} width="35" height="2.5" rx="1" fill="#C8491E" />
      <rect x={VB.w * 0.724} y={groundY - 14} width="2.5" height="17" fill="#C8491E" />
      <rect x={VB.w * 0.752} y={groundY - 14} width="2.5" height="17" fill="#C8491E" />
      <rect x={VB.w * 0.726} y={groundY - 11} width="26" height="3" rx="1" fill="#FF8A4A" />
      </>}

      {/* ── Ferris wheel (far right decoration) ── */}
      {tc.decorations.ferrisWheel?.visible !== false && <>
      <circle cx={VB.w * 0.94} cy={groundY - 25} r="22" fill="none" stroke="#FF8A4A" strokeWidth="2.5" />
      <circle cx={VB.w * 0.94} cy={groundY - 25} r="4" fill="#FFD700" />
      <line x1={VB.w * 0.94} y1={groundY - 47} x2={VB.w * 0.94} y2={groundY - 3} stroke="#FF8A4A" strokeWidth="1.5" />
      <line x1={VB.w * 0.94 - 22} y1={groundY - 25} x2={VB.w * 0.94 + 22} y2={groundY - 25} stroke="#FF8A4A" strokeWidth="1.5" />
      <rect x={VB.w * 0.94 - 8} y={groundY - 3} width="16" height="8" rx="2" fill="#C8491E" />
      {[0, 60, 120, 180, 240, 300].map((a, ai) => (
        <rect key={ai} x={VB.w * 0.94 + Math.cos(a * Math.PI / 180) * 19 - 4} y={groundY - 25 + Math.sin(a * Math.PI / 180) * 19 - 5}
          width="8" height="10" rx="3" fill={["#FF6B6B","#5BBCE6","#FFD700","#7A4FD0","#2FA15A","#E0518A"][ai]} />
      ))}
      </>}

      {/* ── Clock tower (right background) ── */}
      {tc.decorations.clockTower?.visible !== false && <>
      <rect x={VB.w * 0.82} y={groundY - 70} width="18" height="50" rx="2" fill="#D4A574" />
      <rect x={VB.w * 0.82 - 3} y={groundY - 72} width="24" height="6" rx="2" fill="#C8941E" />
      <polygon points={`${VB.w * 0.82 - 3},${groundY - 72} ${VB.w * 0.82 + 9},${groundY - 84} ${VB.w * 0.82 + 21},${groundY - 72}`} fill="#B87D4E" />
      <circle cx={VB.w * 0.82 + 9} cy={groundY - 55} r="6" fill="rgba(255,255,255,.8)" />
      <line x1={VB.w * 0.82 + 9} y1={groundY - 55} x2={VB.w * 0.82 + 9} y2={groundY - 60} stroke="#333" strokeWidth="1" />
      <line x1={VB.w * 0.82 + 9} y1={groundY - 55} x2={VB.w * 0.82 + 12} y2={groundY - 53} stroke="#333" strokeWidth="1" />
      </>}

      {/* ── Street lamps (spread evenly) ── */}
      {tc.decorations.lamps?.visible !== false && <>
      <Lamp x={VB.w * 0.12} y={groundY + 40} s={0.8} />
      <Lamp x={VB.w * 0.35} y={groundY + 40} s={0.8} />
      <Lamp x={VB.w * 0.58} y={groundY + 40} s={0.8} />
      <Lamp x={VB.w * 0.82} y={groundY + 40} s={0.8} />
      </>}

      {/* ── Benches ── */}
      {tc.decorations.benches?.visible !== false && <>
      <rect x={VB.w * 0.28} y={groundY + 8} width="20" height="2.5" rx="1" fill="#8B6B3E" />
      <rect x={VB.w * 0.282} y={groundY + 10.5} width="2.5" height="6" fill="#6B4F2B" />
      <rect x={VB.w * 0.298} y={groundY + 10.5} width="2.5" height="6" fill="#6B4F2B" />
      <rect x={VB.w * 0.65} y={groundY + 10} width="20" height="2.5" rx="1" fill="#8B6B3E" />
      <rect x={VB.w * 0.652} y={groundY + 12.5} width="2.5" height="6" fill="#6B4F2B" />
      <rect x={VB.w * 0.668} y={groundY + 12.5} width="2.5" height="6" fill="#6B4F2B" />
      </>}

      {/* ── Hot air balloon ── */}
      {tc.decorations.hotAirBalloon?.visible !== false &&
      <g style={{ animation: "lwBalloon 6s ease-in-out infinite" }}>
        <ellipse cx={VB.w * 0.12} cy={VB.h * 0.15} rx={VB.w * 0.03} ry={VB.h * 0.05} fill="#FF6B6B" />
        <ellipse cx={VB.w * 0.12} cy={VB.h * 0.145} rx={VB.w * 0.024} ry={VB.h * 0.042} fill="#FF8A8A" />
        <ellipse cx={VB.w * 0.12} cy={VB.h * 0.13} rx={VB.w * 0.012} ry={VB.h * 0.02} fill="#FFE066" opacity=".45" />
        <line x1={VB.w * 0.11} y1={VB.h * 0.2} x2={VB.w * 0.115} y2={VB.h * 0.22} stroke="#8B6B3E" strokeWidth="1" />
        <line x1={VB.w * 0.13} y1={VB.h * 0.2} x2={VB.w * 0.125} y2={VB.h * 0.22} stroke="#8B6B3E" strokeWidth="1" />
        <rect x={VB.w * 0.112} y={VB.h * 0.22} width={VB.w * 0.018} height={VB.h * 0.016} rx="2" fill="#C8941E" />
      </g>}

      {/* ── Signpost ── */}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#8B6B3E" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#FFE066" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#5A3D1E" }}>{signpostLabel}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#FF8A4A" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{signpostSublabel}</text>

      {/* ── BUILDINGS ── */}
      {BUILDINGS.map((b, i) => (
        <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)}
          isSel={selected === i} isCur={i === currentIdx} label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]}
          onSelect={() => setSelected(i)} />
      ))}

      {/* ── Cabi mascot moves to selected building ── */}
      {(() => {
        const cb = BUILDINGS[mascotAt] || BUILDINGS[0];
        return (
          <foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28"
            style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}>
            <div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}>
              <CabiMascot mood="happy" size={26} />
            </div>
          </foreignObject>
        );
      })()}

      {/* ── Flag on castle (last building) ── */}
      {tc.decorations.flag?.visible !== false && (() => {
        const lb = BUILDINGS[6];
        const fx = lb.x + lb.w / 2;
        const fy = lb.y - lb.h - 28;
        return (
          <g>
            <rect x={fx - 1} y={fy} width="2.5" height="18" fill="#8B6B3E" />
            <polygon points={`${fx + 1.5},${fy} ${fx + 18},${fy + 6} ${fx + 1.5},${fy + 12}`}
              fill="#FF6B35" style={{ animation: "lwFlag 3s ease-in-out infinite", transformOrigin: `${fx + 1.5}px ${fy + 6}px` }} />
          </g>
        );
      })()}
    </svg>
  );
}

/* ── Building component ── */
/* Subject-to-building-shape mapping */
const KIND_FOR_SUBJECT = {
  math: "mathtower", english: "library", reading: "library", arabic: "library",
  science: "lab", coding: "studio", art: "artstudio", general: "house",
};

function Building({ b, stop, idx, color, emoji, isSel, isCur, label, onSelect }) {
  const isEmpty = stop.status === "empty";
  const isCastleSlot = idx === 6;
  const cx = b.x + b.w / 2, top = b.y - b.h, w = b.w, h = b.h;

  const labelPill = (
    <g>
      <rect x={cx - 22} y={b.y + 1} width="44" height="11" rx="5" fill="rgba(255,255,255,.9)" />
      <text x={cx} y={b.y + 9} textAnchor="middle"
        style={{ fontFamily: FF, fontWeight: 700, fontSize: 6, fill: "#3A3530", userSelect: "none", pointerEvents: "none" }}>
        {label.length > 9 ? label.substring(0, 8) + "…" : label}
      </text>
    </g>
  );
  const selGlow = isSel && (
    <rect x={b.x - 3} y={top - 3} width={w + 6} height={h + 6} rx="8" fill="none"
      stroke="#F5B31E" strokeWidth="2.5" strokeDasharray="6 4" opacity=".8" />
  );
  const curPulse = isCur && (
    <rect x={b.x - 6} y={top - 24} width={w + 12} height={h + 36} rx="12" fill="none"
      stroke="#FF8A4C" strokeWidth="2.5" opacity=".5">
      <animate attributeName="opacity" values=".5;.15;.5" dur="2s" repeatCount="indefinite" />
    </rect>
  );
  const doneBadge = stop.status === "done" && (
    <>
      <circle cx={b.x + w - 1} cy={top + 1} r="5" fill="#37A65B" stroke="#fff" strokeWidth="1" />
      <text x={b.x + w - 1} y={top + 4} textAnchor="middle" fill="#fff"
        style={{ fontSize: 7, fontWeight: 700, pointerEvents: "none" }}>✓</text>
      <text x={cx} y={top - 16} textAnchor="middle" fontSize="10"
        style={{ userSelect: "none", pointerEvents: "none", animation: `lwStar 4s ease-in-out ${idx * 0.5}s infinite` }}>⭐</text>
    </>
  );
  const sparkles = isCur && [
    { dx: -10, dy: -8, d: 0 }, { dx: w + 6, dy: -12, d: 0.4 },
    { dx: -8, dy: h * 0.3, d: 0.8 }, { dx: w + 4, dy: h * 0.4, d: 1.2 },
  ].map((sp, si) => (
    <text key={si} x={b.x + sp.dx} y={top + sp.dy} fontSize="6"
      style={{ userSelect: "none", pointerEvents: "none", animation: `lwSparkle 3s ease-in-out ${sp.d}s infinite` }}>✨</text>
  ));
  const shadow = <ellipse cx={cx} cy={b.y + 6} rx={w / 2 + 3} ry={4} fill="rgba(0,0,0,.11)" />;

  return (
    <g style={{ cursor: "pointer", opacity: isEmpty && !isCastleSlot ? 0.55 : 1 }} onClick={onSelect}>
      {shadow}
      {isCastleSlot
        ? castleArt(b, cx, top, w, h, isEmpty)
        : (isEmpty
            ? lockedArt(b, cx, top, w, h)
            : subjectArt(KIND_FOR_SUBJECT[(stop.game?.subject || "general").toLowerCase()] || "house",
                         b, cx, top, w, h, color, emoji))}
      {doneBadge}
      {sparkles}
      {labelPill}
      {selGlow}
      {curPulse}
    </g>
  );
}

function subjectArt(kind, b, cx, top, w, h, color, emoji) {
  const dk = darken(color, 30), lt = lighten(color, 35);
  const rim = <rect x={b.x} y={top} width={3} height={h} rx="1.5" fill={lt} opacity=".7" />;
  const sign = <><circle cx={cx} cy={top + h * 0.42} r={7.5} fill="#fff" /><text x={cx} y={top + h * 0.42 + 3} textAnchor="middle" fontSize="8.5" style={{ pointerEvents: "none" }}>{emoji}</text></>;

  if (kind === "library") return (<g><rect x={b.x} y={top + h * 0.18} width={w} height={h * 0.82} rx="4" fill={color} />{rim}<rect x={b.x - 2} y={top + h * 0.1} width={w + 4} height={h * 0.14} rx="2" fill={dk} />{[0,1,2,3,4].map(i => <rect key={i} x={b.x + 2 + i * (w - 4) / 5} y={top} width={(w - 4) / 5 - 1.5} height={h * 0.16} rx="1" fill={["#F4A12B","#2FA15A","#E0518A","#13B6C9","#EE5F2B"][i]} />)}<rect x={cx - w * 0.12} y={b.y - h * 0.3} width={w * 0.24} height={h * 0.3} rx="1.5" fill={dk} />{sign}</g>);
  if (kind === "lab") return (<g><rect x={b.x} y={top + h * 0.28} width={w} height={h * 0.72} rx="4" fill={color} />{rim}<path d={`M ${b.x - 2} ${top + h * 0.3} A ${w / 2 + 2} ${h * 0.3} 0 0 1 ${b.x + w + 2} ${top + h * 0.3} Z`} fill={dk} /><rect x={cx - 1} y={top - 9} width="2" height="9" fill="#9AA" /><circle cx={cx} cy={top - 10} r="3" fill="#13B6C9" />{sign}</g>);
  if (kind === "studio") return (<g><rect x={b.x} y={top + h * 0.16} width={w} height={h * 0.84} rx="4" fill={color} />{rim}<rect x={b.x - 2} y={top + h * 0.08} width={w + 4} height={h * 0.12} rx="2" fill={dk} /><rect x={b.x + w * 0.18} y={top + h * 0.3} width={w * 0.64} height={h * 0.34} rx="2" fill="#1B1340" />{[0,1,2].map(i => <rect key={i} x={b.x + w * 0.24} y={top + h * 0.36 + i * h * 0.08} width={w * (0.3 + 0.18 * ((i + 1) % 2))} height={1.6} rx="0.8" fill={["#5BE8C8","#FFD66B","#7AA8FF"][i]} />)}<rect x={cx - 1} y={top - 10} width="2" height="10" fill="#9AA" /><circle cx={cx} cy={top - 11} r="2.6" fill="#FFD700" />{sign}</g>);
  if (kind === "artstudio") return (<g><rect x={b.x} y={top + h * 0.22} width={w} height={h * 0.78} rx="4" fill={color} />{rim}<polygon points={`${b.x - 3},${top + h * 0.24} ${cx},${top - 12} ${b.x + w + 3},${top + h * 0.24}`} fill={dk} /><circle cx={b.x + w * 0.32} cy={top - 2} r="2.2" fill="#FFD66B" /><circle cx={cx} cy={top - 6} r="2.2" fill="#5BBCE6" /><circle cx={b.x + w * 0.68} cy={top - 2} r="2.2" fill="#2FA15A" />{sign}</g>);
  if (kind === "mathtower") return (<g><rect x={b.x} y={b.y - h * 0.42} width={w} height={h * 0.42} rx="3" fill={color} />{rim}<rect x={b.x + w * 0.1} y={b.y - h * 0.74} width={w * 0.8} height={h * 0.34} rx="3" fill={lighten(color, 18)} /><rect x={b.x + w * 0.22} y={b.y - h * 1.02} width={w * 0.56} height={h * 0.3} rx="3" fill={lighten(color, 34)} />{["3","2","1"].map((d, i) => <text key={i} x={cx} y={b.y - h * (0.16 + i * 0.32)} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 8, fill: "#fff", pointerEvents: "none" }}>{d}</text>)}</g>);
  return (<g><rect x={b.x} y={top} width={w} height={h} rx="4" fill={color} />{rim}<polygon points={`${b.x - 3},${top} ${cx},${top - 10} ${b.x + w + 3},${top}`} fill={dk} />{sign}</g>);
}

function lockedArt(b, cx, top, w, h) {
  return (<g style={{ opacity: .72 }}><rect x={b.x} y={top} width={w} height={h} rx="4" fill="#C9BBAE" /><polygon points={`${b.x - 3},${top} ${cx},${top - 9} ${b.x + w + 3},${top}`} fill="#B0A294" /><circle cx={cx} cy={top + h * 0.45} r="7" fill="#fff" opacity=".85" /><text x={cx} y={top + h * 0.45 + 3.5} textAnchor="middle" fontSize="9" style={{ pointerEvents: "none" }}>🔒</text></g>);
}

function castleArt(b, cx, top, w, h, isEmpty) {
  const gold = "#F4C150", goldD = "#C8941E", goldL = "#FFE08A";
  return (<g>
    <circle cx={cx} cy={top + h * 0.4} r={w * 1.05} fill="url(#castleGlow)" />
    {[b.x - 4, b.x + w - 4].map((tx, i) => (<g key={i}><rect x={tx} y={top - 6} width="8" height={h + 6} rx="2" fill={gold} /><rect x={tx} y={top - 6} width="2.5" height={h + 6} fill={goldL} opacity=".7" /><polygon points={`${tx - 2},${top - 6} ${tx + 4},${top - 15} ${tx + 10},${top - 6}`} fill="#E0561F" /><rect x={tx + 3.4} y={top - 22} width="1.4" height="8" fill="#8B6B3E" /><polygon points={`${tx + 4.8},${top - 22} ${tx + 12},${top - 18.5} ${tx + 4.8},${top - 15}`} fill="#FF6B35" /></g>))}
    <rect x={b.x + 3} y={top} width={w - 6} height={h} rx="3" fill={gold} /><rect x={b.x + 3} y={top} width="3" height={h} fill={goldL} opacity=".7" />
    <rect x={b.x + 3} y={top - 4} width={w - 6} height="5" fill={goldD} />
    {[0,1,2].map(i => <rect key={i} x={b.x + 5 + i * (w - 10) / 3} y={top - 7} width={(w - 10) / 3 - 2} height="4" fill={goldD} />)}
    <path d={`M ${cx - w * 0.16} ${b.y} L ${cx - w * 0.16} ${b.y - h * 0.32} A ${w * 0.16} ${w * 0.16} 0 0 1 ${cx + w * 0.16} ${b.y - h * 0.32} L ${cx + w * 0.16} ${b.y} Z`} fill="#7A4B12" />
    <polygon points={`${cx},${top + h * 0.34} ${cx - 5},${top + h * 0.46} ${cx},${top + h * 0.6} ${cx + 5},${top + h * 0.46}`} fill="#5BE0FF" stroke="#fff" strokeWidth="0.8" />
    {isEmpty && <><circle cx={cx} cy={top - 13} r="6.5" fill="#fff" opacity=".9" /><text x={cx} y={top - 9.5} textAnchor="middle" fontSize="8" style={{ pointerEvents: "none" }}>🔒</text></>}
    <rect x={cx - 26} y={b.y + 2} width="52" height="13" rx="6.5" fill="#E0561F" />
    <text x={cx} y={b.y + 11} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 6.5, fill: "#fff", pointerEvents: "none" }}>★ Final Goal</text>
  </g>);
}

function ParallaxHills({ VB, groundY }) {
  return (<g>
    <path d={`M0 ${groundY - 20} Q ${VB.w * 0.25} ${groundY - 150} ${VB.w * 0.5} ${groundY - 60} T ${VB.w} ${groundY - 40} L ${VB.w} ${groundY} L0 ${groundY} Z`} fill="#A9D9B0" opacity=".55" />
    <path d={`M0 ${groundY - 10} Q ${VB.w * 0.3} ${groundY - 110} ${VB.w * 0.6} ${groundY - 40} T ${VB.w} ${groundY - 20} L ${VB.w} ${groundY} L0 ${groundY} Z`} fill="#8FCB8C" opacity=".7" />
    <path d={`M0 ${groundY} Q ${VB.w * 0.2} ${groundY - 70} ${VB.w * 0.45} ${groundY - 15} T ${VB.w} ${groundY - 5} L ${VB.w} ${groundY} Z`} fill="#79BE62" />
  </g>);
}

/* ── Helpers ── */

function scatterTrees(VB, isPortrait) {
  const gy = VB.h * 0.62;
  if (isPortrait) return [
    { x: 30, y: gy + 15, s: 1 }, { x: VB.w - 40, y: gy + 10, s: 0.9 },
    { x: 80, y: gy - 10, s: 0.7 }, { x: VB.w - 60, y: gy - 5, s: 0.8 },
    { x: 250, y: gy + 25, s: 0.75 }, { x: VB.w - 120, y: gy + 20, s: 0.85 },
    { x: 50, y: 550, s: 0.6 }, { x: VB.w - 50, y: 480, s: 0.55 },
    { x: 30, y: 350, s: 0.5 }, { x: VB.w - 30, y: 300, s: 0.5 },
    { x: 500, y: gy + 8, s: 0.65 }, { x: 470, y: 650, s: 0.6 },
  ];
  return [
    { x: 30, y: gy + 12, s: 1 }, { x: VB.w - 30, y: gy + 10, s: 0.95 },
    { x: 150, y: gy + 20, s: 0.8 }, { x: VB.w - 100, y: gy + 15, s: 0.85 },
    { x: 350, y: gy + 5, s: 0.7 }, { x: 680, y: gy + 8, s: 0.75 },
    { x: 860, y: gy - 5, s: 0.8 }, { x: 50, y: 380, s: 0.6 },
    { x: VB.w - 40, y: 300, s: 0.65 }, { x: 180, y: 340, s: 0.5 },
    { x: 830, y: 240, s: 0.55 }, { x: VB.w - 60, y: 420, s: 0.7 },
    { x: VB.w - 80, y: 180, s: 0.5 }, { x: 50, y: 250, s: 0.45 },
    { x: 450, y: gy + 15, s: 0.65 }, { x: VB.w - 150, y: gy + 5, s: 0.7 },
  ];
}

function Tree({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${s})`}>
      <rect x="-2.5" y="-4" width="5" height="15" rx="2" fill="#6B4A2E" />
      <ellipse cx="0" cy="-14" rx="12" ry="15" fill="#3E8E4E" />
      <ellipse cx="0" cy="-18" rx="8" ry="11" fill="#57A55F" />
      <ellipse cx="-3" cy="-21" rx="4" ry="6" fill="#6DBF5A" opacity=".6" />
    </g>
  );
}

function Cloud({ cx, cy, rx }) {
  return (
    <g>
      <ellipse cx={cx} cy={cy} rx={rx} ry={rx * 0.38} fill="white" opacity=".85" />
      <ellipse cx={cx + rx * 0.35} cy={cy - rx * 0.1} rx={rx * 0.65} ry={rx * 0.3} fill="white" opacity=".9" />
    </g>
  );
}

function Lamp({ x, y, s = 1 }) {
  return (
    <g transform={`translate(${x},${y}) scale(${s})`}>
      <rect x="-1.5" y="-28" width="3" height="28" fill="#555" />
      <ellipse cx="0" cy="-28" rx="5" ry="4" fill="#FFE066" opacity=".7" />
      <rect x="-4" y="-31" width="8" height="3.5" rx="1.5" fill="#444" />
    </g>
  );
}

function NavBtn({ icon, onClick }) {
  return (
    <button onClick={onClick}
      className="w-7 h-7 rounded-lg bg-cream/60 hover:bg-cream border-0 cursor-pointer flex items-center justify-center text-[13px] shrink-0 transition-colors">
      {icon}
    </button>
  );
}

/* ── Pirate Island theme ── */
function IslandScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const { t } = useTranslation();
  const VB = isPortrait
    ? { w: 600, h: 900, ar: "xMidYMid slice" }
    : { w: 1000, h: 600, ar: "xMidYMid slice" };

  const BUILDINGS = isPortrait ? [
    { x: 100, y: 720, w: 40, h: 44 },
    { x: 390, y: 650, w: 42, h: 46 },
    { x: 120, y: 570, w: 42, h: 46 },
    { x: 400, y: 490, w: 40, h: 44 },
    { x: 110, y: 400, w: 42, h: 46 },
    { x: 390, y: 310, w: 42, h: 46 },
    { x: 250, y: 210, w: 46, h: 50 },
  ] : [
    { x: 80,  y: 470, w: 42, h: 46 },
    { x: 280, y: 430, w: 44, h: 48 },
    { x: 500, y: 390, w: 44, h: 48 },
    { x: 760, y: 350, w: 42, h: 46 },
    { x: 600, y: 265, w: 42, h: 46 },
    { x: 360, y: 200, w: 44, h: 48 },
    { x: 645, y: 125, w: 48, h: 52 },
  ];

  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  const tc = mergeDeep(SCENE_DEFAULTS.island, config);
  const colorOf = (i) => {
    const override = tc.buildings?.[(i + 1).toString()]?.color;
    return override || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B";
  };
  const signpostLabel = tc.signpost.label === "Pirate Isle" ? t("kids.games.map.pirateIsle") : tc.signpost.label;
  const signpostSublabel = tc.signpost.sublabel === "Explore!" ? t("kids.games.map.explore") : tc.signpost.sublabel;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs>
        <linearGradient id="islSky" x1="0" y1="0" x2="0" y2="1">
          {tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
        </linearGradient>
      </defs>

      <rect width={VB.w} height={VB.h} fill="url(#islSky)" />

      {/* Ocean waves */}
      <rect x="0" y={groundY + 40} width={VB.w} height={VB.h - groundY - 40} fill="#1CA5C9" />
      <ellipse cx={VB.w * 0.3} cy={groundY + 55} rx={VB.w * 0.4} ry={12} fill="#2BBFD9" />
      <ellipse cx={VB.w * 0.7} cy={groundY + 70} rx={VB.w * 0.35} ry={10} fill="#37CADF" />

      {/* Sandy island ground */}
      <ellipse cx={VB.w * 0.5} cy={groundY + 10} rx={VB.w * 0.48} ry={VB.h * 0.08} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.5} cy={groundY} rx={VB.w * 0.46} ry={VB.h * 0.065} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.35} cy={groundY - 5} rx={VB.w * 0.3} ry={VB.h * 0.05} fill={tc.ground.accent} />
      <ellipse cx={VB.w * 0.65} cy={groundY + 2} rx={VB.w * 0.25} ry={VB.h * 0.04} fill={tc.ground.accent} />

      {/* Clouds */}
      {tc.decorations.clouds?.visible !== false && <>
      <Cloud cx={VB.w * 0.2} cy={VB.h * 0.07} rx={VB.w * 0.06} />
      <Cloud cx={VB.w * 0.6} cy={VB.h * 0.05} rx={VB.w * 0.07} />
      <Cloud cx={VB.w * 0.85} cy={VB.h * 0.09} rx={VB.w * 0.05} />
      </>}

      {/* Sun */}
      {tc.decorations.sun?.visible !== false && <>
      <circle cx={VB.w * 0.88} cy={VB.h * 0.1} r={VB.w * 0.04} fill="#FFE066" opacity=".9" />
      <circle cx={VB.w * 0.88} cy={VB.h * 0.1} r={VB.w * 0.03} fill="#FFD700" />
      </>}

      {/* Palm trees */}
      {tc.decorations.palmTrees?.visible !== false &&
      [VB.w * 0.08, VB.w * 0.22, VB.w * 0.78, VB.w * 0.92].map((px, pi) => (
        <g key={pi}>
          <rect x={px - 2} y={groundY - 40} width="4" height="42" rx="2" fill="#8B6B3E" />
          <ellipse cx={px + 12} cy={groundY - 42} rx="16" ry="6" fill="#3E8E4E" />
          <ellipse cx={px - 10} cy={groundY - 38} rx="14" ry="5" fill="#4CAF50" />
          <ellipse cx={px + 8} cy={groundY - 48} rx="12" ry="5" fill="#57A55F" />
        </g>
      ))}

      {/* Treasure chest */}
      {tc.decorations.treasureChest?.visible !== false && <>
      <rect x={VB.w * 0.06} y={groundY + 2} width="22" height="14" rx="3" fill="#8B6B3E" />
      <rect x={VB.w * 0.06} y={groundY} width="22" height="5" rx="2" fill="#C8941E" />
      <circle cx={VB.w * 0.06 + 11} cy={groundY + 10} r="3" fill="#FFD700" />
      </>}

      {/* Pirate ship (right side) */}
      {tc.decorations.pirateShip?.visible !== false && <>
      <polygon points={`${VB.w * 0.82},${groundY + 50} ${VB.w * 0.95},${groundY + 50} ${VB.w * 0.97},${groundY + 65} ${VB.w * 0.8},${groundY + 65}`} fill="#8B6B3E" />
      <rect x={VB.w * 0.88} y={groundY + 20} width="3" height="30" fill="#6B4F2B" />
      <polygon points={`${VB.w * 0.91},${groundY + 22} ${VB.w * 0.91},${groundY + 42} ${VB.w * 0.97},${groundY + 32}`} fill="#fff" opacity=".85" />
      <text x={VB.w * 0.87} y={groundY + 60} fontSize="10" style={{ userSelect: "none" }}>🏴‍☠️</text>
      </>}

      {/* Winding path */}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />

      {/* Buildings (hut-style) */}
      {BUILDINGS.map((b, i) => (
        <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)}
          isSel={selected === i} isCur={i === currentIdx}
          label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]}
          onSelect={() => setSelected(i)} />
      ))}

      {/* Mascot */}
      {(() => {
        const cb = BUILDINGS[mascotAt] || BUILDINGS[0];
        return (
          <foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28"
            style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}>
            <div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}>
              <CabiMascot mood="happy" size={26} />
            </div>
          </foreignObject>
        );
      })()}

      {/* Signpost */}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#8B6B3E" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#FFE066" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#5A3D1E" }}>{signpostLabel}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#FF8A4A" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{signpostSublabel}</text>
    </svg>
  );
}

/* ── Space Station theme ── */
function SpaceScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const { t } = useTranslation();
  const VB = isPortrait
    ? { w: 600, h: 900, ar: "xMidYMid slice" }
    : { w: 1000, h: 600, ar: "xMidYMid slice" };

  const BUILDINGS = isPortrait ? [
    { x: 100, y: 720, w: 40, h: 44 },
    { x: 390, y: 650, w: 42, h: 46 },
    { x: 120, y: 570, w: 42, h: 46 },
    { x: 400, y: 490, w: 40, h: 44 },
    { x: 110, y: 400, w: 42, h: 46 },
    { x: 390, y: 310, w: 42, h: 46 },
    { x: 250, y: 210, w: 46, h: 50 },
  ] : [
    { x: 80,  y: 470, w: 42, h: 46 },
    { x: 280, y: 430, w: 44, h: 48 },
    { x: 500, y: 390, w: 44, h: 48 },
    { x: 760, y: 350, w: 42, h: 46 },
    { x: 600, y: 265, w: 42, h: 46 },
    { x: 360, y: 200, w: 44, h: 48 },
    { x: 645, y: 125, w: 48, h: 52 },
  ];

  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  const tc = mergeDeep(SCENE_DEFAULTS.space, config);
  const colorOf = (i) => {
    const override = tc.buildings?.[(i + 1).toString()]?.color;
    return override || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B";
  };
  const signpostLabel = tc.signpost.label === "Star Base" ? t("kids.games.map.starBase") : tc.signpost.label;
  const signpostSublabel = tc.signpost.sublabel === "Blast off!" ? t("kids.games.map.blastOff") : tc.signpost.sublabel;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs>
        <linearGradient id="spSky" x1="0" y1="0" x2="0" y2="1">
          {tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
        </linearGradient>
      </defs>

      <rect width={VB.w} height={VB.h} fill="url(#spSky)" />

      {/* Stars */}
      {tc.decorations.stars?.visible !== false &&
      Array.from({ length: 40 }, (_, i) => (
        <circle key={i} cx={(i * 97 + 31) % VB.w} cy={(i * 53 + 11) % (groundY - 20)}
          r={i % 3 === 0 ? 1.5 : 1} fill="#fff" opacity={0.4 + (i % 5) * 0.12} />
      ))}

      {/* Planet */}
      {tc.decorations.planet?.visible !== false && <>
      <circle cx={VB.w * 0.85} cy={VB.h * 0.12} r={VB.w * 0.05} fill="#FF8A4A" opacity=".7" />
      <circle cx={VB.w * 0.85} cy={VB.h * 0.12} r={VB.w * 0.04} fill="#FFB74D" opacity=".5" />
      <ellipse cx={VB.w * 0.85} cy={VB.h * 0.12} rx={VB.w * 0.08} ry={VB.h * 0.01} fill="none" stroke="#FFD700" strokeWidth="1.5" opacity=".4" />
      </>}

      {/* Nebula glow */}
      {tc.decorations.nebula?.visible !== false && <>
      <ellipse cx={VB.w * 0.3} cy={VB.h * 0.15} rx={VB.w * 0.12} ry={VB.h * 0.08} fill="#7A4FD0" opacity=".12" />
      <ellipse cx={VB.w * 0.65} cy={VB.h * 0.25} rx={VB.w * 0.08} ry={VB.h * 0.06} fill="#E0518A" opacity=".08" />
      </>}

      {/* Station platform */}
      <rect x={VB.w * 0.02} y={groundY} width={VB.w * 0.96} height={VB.h - groundY} rx="4" fill={tc.ground.primary} />
      <rect x={VB.w * 0.02} y={groundY} width={VB.w * 0.96} height="6" rx="3" fill={tc.ground.secondary} />
      <rect x={VB.w * 0.05} y={groundY + 8} width={VB.w * 0.9} height="2" fill={tc.ground.accent} opacity=".5" />
      <rect x={VB.w * 0.08} y={groundY + 16} width={VB.w * 0.84} height="1.5" fill={tc.ground.accent} opacity=".3" />

      {/* Glowing path */}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />

      {/* Rockets / decorations */}
      {tc.decorations.rocket?.visible !== false &&
      <g transform={`translate(${VB.w * 0.06},${groundY - 50})`}>
        <rect x="-5" y="10" width="10" height="30" rx="3" fill="#B0B0C0" />
        <polygon points="-5,10 0,-5 5,10" fill="#FF6B6B" />
        <rect x="-8" y="35" width="6" height="8" rx="1" fill="#FF8A4A" />
        <rect x="2" y="35" width="6" height="8" rx="1" fill="#FF8A4A" />
        <ellipse cx="0" cy="44" rx="4" ry="6" fill="#FFE066" opacity=".6" />
      </g>}

      {/* Satellite */}
      {tc.decorations.satellite?.visible !== false &&
      <g style={{ animation: "lwBalloon 8s ease-in-out infinite" }}>
        <rect x={VB.w * 0.45} y={VB.h * 0.06} width="16" height="8" rx="3" fill="#B0B0C0" />
        <rect x={VB.w * 0.45 - 14} y={VB.h * 0.06 + 1} width="14" height="6" rx="1" fill="#5BBCE6" opacity=".7" />
        <rect x={VB.w * 0.45 + 16} y={VB.h * 0.06 + 1} width="14" height="6" rx="1" fill="#5BBCE6" opacity=".7" />
      </g>}

      {/* Alien friends */}
      {tc.decorations.aliens?.visible !== false && <>
      <text x={VB.w * 0.92} y={groundY + 20} fontSize="14" style={{ userSelect: "none" }}>👾</text>
      <text x={VB.w * 0.15} y={groundY + 25} fontSize="12" style={{ userSelect: "none" }}>🛸</text>
      </>}

      {/* Buildings (space-pod style but uses same Building component) */}
      {BUILDINGS.map((b, i) => (
        <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)}
          isSel={selected === i} isCur={i === currentIdx}
          label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]}
          onSelect={() => setSelected(i)} />
      ))}

      {/* Mascot */}
      {(() => {
        const cb = BUILDINGS[mascotAt] || BUILDINGS[0];
        return (
          <foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28"
            style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}>
            <div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}>
              <CabiMascot mood="happy" size={26} />
            </div>
          </foreignObject>
        );
      })()}

      {/* Signpost */}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#5A4FA0" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#7A4FD0" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#E0D0FF" }}>{signpostLabel}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#E0518A" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{signpostSublabel}</text>
    </svg>
  );
}

/* ── Jungle theme ── */
function JungleScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const VB = isPortrait ? { w: 600, h: 900, ar: "xMidYMid slice" } : { w: 1000, h: 600, ar: "xMidYMid slice" };
  const BUILDINGS = isPortrait ? [
    { x: 100, y: 795, w: 40, h: 44 }, { x: 390, y: 725, w: 42, h: 46 }, { x: 120, y: 640, w: 42, h: 46 },
    { x: 400, y: 550, w: 40, h: 44 }, { x: 110, y: 450, w: 42, h: 46 }, { x: 390, y: 360, w: 42, h: 46 },
    { x: 250, y: 240, w: 46, h: 50 },
  ] : [
    { x: 80, y: 495, w: 42, h: 46 }, { x: 280, y: 458, w: 44, h: 48 }, { x: 500, y: 418, w: 44, h: 48 },
    { x: 760, y: 375, w: 42, h: 46 }, { x: 600, y: 285, w: 42, h: 46 }, { x: 360, y: 215, w: 44, h: 48 },
    { x: 645, y: 135, w: 48, h: 52 },
  ];
  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const tc = mergeDeep(SCENE_DEFAULTS.jungle, config);
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const colorOf = (i) => { const o = tc.buildings?.[(i+1).toString()]?.color; return o || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B"; };
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs><linearGradient id="jgSky" x1="0" y1="0" x2="0" y2="1">{tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}</linearGradient></defs>
      <rect width={VB.w} height={VB.h} fill="url(#jgSky)" />
      {tc.decorations.mist?.visible !== false && <><ellipse cx={VB.w * 0.2} cy={groundY - 40} rx={VB.w * 0.15} ry={20} fill="#fff" opacity=".15" /><ellipse cx={VB.w * 0.7} cy={groundY - 30} rx={VB.w * 0.12} ry={15} fill="#fff" opacity=".12" /></>}
      <rect x="0" y={groundY} width={VB.w} height={VB.h - groundY} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.3} cy={groundY} rx={VB.w * 0.35} ry={VB.h * 0.06} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.75} cy={groundY} rx={VB.w * 0.3} ry={VB.h * 0.055} fill={tc.ground.accent} />
      {tc.decorations.vines?.visible !== false && [0.15, 0.4, 0.65, 0.85].map((px, i) => <g key={i}><rect x={VB.w * px - 3} y={groundY - 60} width="6" height="62" rx="3" fill="#5A3D1E" /><ellipse cx={VB.w * px + 14} cy={groundY - 55} rx="18" ry="8" fill="#1B7A3D" /><ellipse cx={VB.w * px - 12} cy={groundY - 45} rx="16" ry="7" fill="#228B3B" /><ellipse cx={VB.w * px + 8} cy={groundY - 65} rx="14" ry="7" fill="#2DA44E" /></g>)}
      {tc.decorations.parrots?.visible !== false && <><text x={VB.w * 0.25} y={groundY - 70} fontSize="16" style={{ userSelect: "none" }}>🦜</text><text x={VB.w * 0.7} y={groundY - 80} fontSize="14" style={{ userSelect: "none" }}>🦜</text></>}
      {tc.decorations.monkeys?.visible !== false && <><text x={VB.w * 0.5} y={groundY - 50} fontSize="14" style={{ userSelect: "none" }}>🐒</text><text x={VB.w * 0.88} y={groundY - 40} fontSize="12" style={{ userSelect: "none" }}>🐒</text></>}
      {tc.decorations.waterfall?.visible !== false && <><rect x={VB.w * 0.92} y={groundY - 80} width="12" height="82" rx="4" fill="#5BBCE6" opacity=".7" /><ellipse cx={VB.w * 0.925} cy={groundY + 4} rx="18" ry="6" fill="#5BBCE6" opacity=".5" /></>}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />
      {BUILDINGS.map((b, i) => <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)} isSel={selected === i} isCur={i === currentIdx} label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]} onSelect={() => setSelected(i)} />)}
      {(() => { const cb = BUILDINGS[mascotAt] || BUILDINGS[0]; return (<foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28" style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}><div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}><CabiMascot mood="happy" size={26} /></div></foreignObject>); })()}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#5A3D1E" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#2DA44E" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#fff" }}>{tc.signpost.label}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#1B7A3D" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{tc.signpost.sublabel}</text>
    </svg>
  );
}

/* ── Underwater theme ── */
function UnderwaterScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const VB = isPortrait ? { w: 600, h: 900, ar: "xMidYMid slice" } : { w: 1000, h: 600, ar: "xMidYMid slice" };
  const BUILDINGS = isPortrait ? [
    { x: 100, y: 795, w: 40, h: 44 }, { x: 390, y: 725, w: 42, h: 46 }, { x: 120, y: 640, w: 42, h: 46 },
    { x: 400, y: 550, w: 40, h: 44 }, { x: 110, y: 450, w: 42, h: 46 }, { x: 390, y: 360, w: 42, h: 46 },
    { x: 250, y: 240, w: 46, h: 50 },
  ] : [
    { x: 80, y: 495, w: 42, h: 46 }, { x: 280, y: 458, w: 44, h: 48 }, { x: 500, y: 418, w: 44, h: 48 },
    { x: 760, y: 375, w: 42, h: 46 }, { x: 600, y: 285, w: 42, h: 46 }, { x: 360, y: 215, w: 44, h: 48 },
    { x: 645, y: 135, w: 48, h: 52 },
  ];
  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const tc = mergeDeep(SCENE_DEFAULTS.underwater, config);
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const colorOf = (i) => { const o = tc.buildings?.[(i+1).toString()]?.color; return o || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B"; };
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs><linearGradient id="uwSky" x1="0" y1="0" x2="0" y2="1">{tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}</linearGradient></defs>
      <rect width={VB.w} height={VB.h} fill="url(#uwSky)" />
      {tc.decorations.bubbles?.visible !== false && Array.from({ length: 20 }, (_, i) => <circle key={i} cx={(i * 131 + 47) % VB.w} cy={(i * 67 + 30) % VB.h} r={2 + (i % 3) * 1.5} fill="#fff" opacity={0.15 + (i % 4) * 0.05} />)}
      {tc.decorations.lightRays?.visible !== false && <><polygon points={`${VB.w * 0.3},0 ${VB.w * 0.35},${VB.h * 0.5} ${VB.w * 0.25},${VB.h * 0.5}`} fill="#87CEEB" opacity=".06" /><polygon points={`${VB.w * 0.6},0 ${VB.w * 0.67},${VB.h * 0.6} ${VB.w * 0.55},${VB.h * 0.6}`} fill="#87CEEB" opacity=".05" /></>}
      <rect x="0" y={groundY} width={VB.w} height={VB.h - groundY} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.3} cy={groundY + 5} rx={VB.w * 0.35} ry={VB.h * 0.04} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.75} cy={groundY + 8} rx={VB.w * 0.3} ry={VB.h * 0.035} fill={tc.ground.accent} />
      {tc.decorations.coral?.visible !== false && [0.1, 0.3, 0.55, 0.8].map((px, i) => <g key={i}><ellipse cx={VB.w * px} cy={groundY + 8} rx="12" ry="18" fill={["#FF6B6B","#FF8A4A","#E0518A","#FFD700"][i]} opacity=".7" /><ellipse cx={VB.w * px + 8} cy={groundY + 5} rx="8" ry="14" fill={["#FF8A8A","#FFB74D","#FF6BA6","#FFE066"][i]} opacity=".6" /></g>)}
      {tc.decorations.seaweed?.visible !== false && [0.08, 0.22, 0.45, 0.68, 0.9].map((px, i) => <g key={i}><ellipse cx={VB.w * px} cy={groundY - 5} rx="4" ry="25" fill="#2DA44E" opacity=".6" /><ellipse cx={VB.w * px + 6} cy={groundY - 2} rx="3" ry="20" fill="#3FB950" opacity=".5" /></g>)}
      {tc.decorations.fish?.visible !== false && <><text x={VB.w * 0.15} y={VB.h * 0.2} fontSize="18" style={{ userSelect: "none" }}>🐠</text><text x={VB.w * 0.5} y={VB.h * 0.15} fontSize="14" style={{ userSelect: "none" }}>🐡</text><text x={VB.w * 0.8} y={VB.h * 0.25} fontSize="16" style={{ userSelect: "none" }}>🐟</text><text x={VB.w * 0.35} y={VB.h * 0.35} fontSize="12" style={{ userSelect: "none" }}>🦀</text></>}
      {tc.decorations.whale?.visible !== false && <text x={VB.w * 0.7} y={VB.h * 0.1} fontSize="28" style={{ userSelect: "none", opacity: 0.4 }}>🐋</text>}
      {tc.decorations.treasure?.visible !== false && <><rect x={VB.w * 0.88} y={groundY + 5} width="22" height="14" rx="3" fill="#8B6B3E" /><rect x={VB.w * 0.88} y={groundY + 3} width="22" height="5" rx="2" fill="#C8941E" /><circle cx={VB.w * 0.88 + 11} cy={groundY + 12} r="3" fill="#FFD700" /></>}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />
      {BUILDINGS.map((b, i) => <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)} isSel={selected === i} isCur={i === currentIdx} label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]} onSelect={() => setSelected(i)} />)}
      {(() => { const cb = BUILDINGS[mascotAt] || BUILDINGS[0]; return (<foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28" style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}><div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}><CabiMascot mood="happy" size={26} /></div></foreignObject>); })()}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#1CA5C9" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#0E7490" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#E0F7FF" }}>{tc.signpost.label}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#06B6D4" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{tc.signpost.sublabel}</text>
    </svg>
  );
}

/* ── Candy Land theme ── */
function CandyScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const VB = isPortrait ? { w: 600, h: 900, ar: "xMidYMid slice" } : { w: 1000, h: 600, ar: "xMidYMid slice" };
  const BUILDINGS = isPortrait ? [
    { x: 100, y: 795, w: 40, h: 44 }, { x: 390, y: 725, w: 42, h: 46 }, { x: 120, y: 640, w: 42, h: 46 },
    { x: 400, y: 550, w: 40, h: 44 }, { x: 110, y: 450, w: 42, h: 46 }, { x: 390, y: 360, w: 42, h: 46 },
    { x: 250, y: 240, w: 46, h: 50 },
  ] : [
    { x: 80, y: 495, w: 42, h: 46 }, { x: 280, y: 458, w: 44, h: 48 }, { x: 500, y: 418, w: 44, h: 48 },
    { x: 760, y: 375, w: 42, h: 46 }, { x: 600, y: 285, w: 42, h: 46 }, { x: 360, y: 215, w: 44, h: 48 },
    { x: 645, y: 135, w: 48, h: 52 },
  ];
  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const tc = mergeDeep(SCENE_DEFAULTS.candy, config);
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const colorOf = (i) => { const o = tc.buildings?.[(i+1).toString()]?.color; return o || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B"; };
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs><linearGradient id="cdSky" x1="0" y1="0" x2="0" y2="1">{tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}</linearGradient></defs>
      <rect width={VB.w} height={VB.h} fill="url(#cdSky)" />
      {tc.decorations.clouds?.visible !== false && <><Cloud cx={VB.w * 0.15} cy={VB.h * 0.08} rx={VB.w * 0.06} /><Cloud cx={VB.w * 0.55} cy={VB.h * 0.06} rx={VB.w * 0.07} /><Cloud cx={VB.w * 0.85} cy={VB.h * 0.1} rx={VB.w * 0.05} /></>}
      {tc.decorations.rainbow?.visible !== false && <>{[0, 8, 16, 24, 32].map((off, ri) => <ellipse key={ri} cx={VB.w * 0.5} cy={VB.h * 0.2} rx={VB.w * 0.25 - off} ry={VB.h * 0.12 - off * 0.4} fill="none" stroke={["#FF6B6B","#FFB74D","#FFE066","#4CAF50","#5BBCE6"][ri]} strokeWidth="5" opacity=".5" />)}</>}
      <rect x="0" y={groundY} width={VB.w} height={VB.h - groundY} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.3} cy={groundY} rx={VB.w * 0.35} ry={VB.h * 0.06} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.75} cy={groundY} rx={VB.w * 0.3} ry={VB.h * 0.055} fill={tc.ground.accent} />
      {tc.decorations.lollipops?.visible !== false && [0.12, 0.35, 0.6, 0.88].map((px, i) => <g key={i}><rect x={VB.w * px - 1.5} y={groundY - 30} width="3" height="32" fill="#fff" /><circle cx={VB.w * px} cy={groundY - 34} r="10" fill={["#FF6B6B","#5BBCE6","#FFD700","#E0518A"][i]} /><circle cx={VB.w * px} cy={groundY - 34} r="5" fill={["#FF8A8A","#87CEEB","#FFE066","#FF6BA6"][i]} /></g>)}
      {tc.decorations.candyCanes?.visible !== false && <><text x={VB.w * 0.05} y={groundY + 15} fontSize="20" style={{ userSelect: "none" }}>🍬</text><text x={VB.w * 0.92} y={groundY + 12} fontSize="18" style={{ userSelect: "none" }}>🍭</text></>}
      {tc.decorations.gummies?.visible !== false && <><text x={VB.w * 0.2} y={groundY + 18} fontSize="12" style={{ userSelect: "none" }}>🧁</text><text x={VB.w * 0.5} y={groundY + 20} fontSize="14" style={{ userSelect: "none" }}>🍩</text><text x={VB.w * 0.75} y={groundY + 15} fontSize="12" style={{ userSelect: "none" }}>🍪</text></>}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />
      {BUILDINGS.map((b, i) => <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)} isSel={selected === i} isCur={i === currentIdx} label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]} onSelect={() => setSelected(i)} />)}
      {(() => { const cb = BUILDINGS[mascotAt] || BUILDINGS[0]; return (<foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28" style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}><div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}><CabiMascot mood="happy" size={26} /></div></foreignObject>); })()}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#E0518A" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#FF6BA6" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#fff" }}>{tc.signpost.label}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#FFD700" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#E0518A" }}>{tc.signpost.sublabel}</text>
    </svg>
  );
}

/* ── Arctic theme ── */
function ArcticScene({ stops, currentIdx, selected, setSelected, isPortrait, mascotAt, config }) {
  const VB = isPortrait ? { w: 600, h: 900, ar: "xMidYMid slice" } : { w: 1000, h: 600, ar: "xMidYMid slice" };
  const BUILDINGS = isPortrait ? [
    { x: 100, y: 795, w: 40, h: 44 }, { x: 390, y: 725, w: 42, h: 46 }, { x: 120, y: 640, w: 42, h: 46 },
    { x: 400, y: 550, w: 40, h: 44 }, { x: 110, y: 450, w: 42, h: 46 }, { x: 390, y: 360, w: 42, h: 46 },
    { x: 250, y: 240, w: 46, h: 50 },
  ] : [
    { x: 80, y: 495, w: 42, h: 46 }, { x: 280, y: 458, w: 44, h: 48 }, { x: 500, y: 418, w: 44, h: 48 },
    { x: 760, y: 375, w: 42, h: 46 }, { x: 600, y: 285, w: 42, h: 46 }, { x: 360, y: 215, w: 44, h: 48 },
    { x: 645, y: 135, w: 48, h: 52 },
  ];
  const roadPoints = BUILDINGS.map(b => `${b.x + b.w / 2},${b.y + 8}`).join(" ");
  const tc = mergeDeep(SCENE_DEFAULTS.arctic, config);
  const subjectOf = (i) => (stops[i]?.game?.subject || "general").toLowerCase();
  const colorOf = (i) => { const o = tc.buildings?.[(i+1).toString()]?.color; return o || SUBJECT_COLOR[subjectOf(i)] || "#EE5F2B"; };
  const emojiOf = (i) => SUBJECT_EMOJI[subjectOf(i)] || "⭐";
  const groundY = VB.h * 0.62;

  return (
    <svg viewBox={`0 0 ${VB.w} ${VB.h}`} preserveAspectRatio={VB.ar} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
      <defs><linearGradient id="arSky" x1="0" y1="0" x2="0" y2="1">{tc.sky.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}</linearGradient></defs>
      <rect width={VB.w} height={VB.h} fill="url(#arSky)" />
      {tc.decorations.snowflakes?.visible !== false && Array.from({ length: 25 }, (_, i) => <text key={i} x={(i * 113 + 29) % VB.w} y={(i * 71 + 15) % (groundY - 10)} fontSize={6 + (i % 3) * 3} style={{ userSelect: "none", opacity: 0.3 + (i % 4) * 0.1 }}>❄️</text>)}
      {tc.decorations.aurora?.visible !== false && <><ellipse cx={VB.w * 0.3} cy={VB.h * 0.12} rx={VB.w * 0.2} ry={VB.h * 0.06} fill="#4ADE80" opacity=".08" /><ellipse cx={VB.w * 0.6} cy={VB.h * 0.08} rx={VB.w * 0.15} ry={VB.h * 0.04} fill="#06B6D4" opacity=".1" /><ellipse cx={VB.w * 0.5} cy={VB.h * 0.15} rx={VB.w * 0.18} ry={VB.h * 0.05} fill="#7C3AED" opacity=".06" /></>}
      {tc.decorations.icebergs?.visible !== false && <><polygon points={`${VB.w * 0.08},${groundY - 30} ${VB.w * 0.15},${groundY - 80} ${VB.w * 0.22},${groundY - 30}`} fill="#D6EFF5" opacity=".7" /><polygon points={`${VB.w * 0.78},${groundY - 20} ${VB.w * 0.85},${groundY - 70} ${VB.w * 0.92},${groundY - 20}`} fill="#E0F2FE" opacity=".6" /></>}
      <rect x="0" y={groundY} width={VB.w} height={VB.h - groundY} fill={tc.ground.primary} />
      <ellipse cx={VB.w * 0.3} cy={groundY} rx={VB.w * 0.35} ry={VB.h * 0.06} fill={tc.ground.secondary} />
      <ellipse cx={VB.w * 0.75} cy={groundY} rx={VB.w * 0.3} ry={VB.h * 0.055} fill={tc.ground.accent} />
      {tc.decorations.penguins?.visible !== false && <><text x={VB.w * 0.1} y={groundY + 20} fontSize="16" style={{ userSelect: "none" }}>🐧</text><text x={VB.w * 0.3} y={groundY + 18} fontSize="12" style={{ userSelect: "none" }}>🐧</text><text x={VB.w * 0.88} y={groundY + 16} fontSize="14" style={{ userSelect: "none" }}>🐧</text></>}
      {tc.decorations.polarbear?.visible !== false && <text x={VB.w * 0.6} y={groundY + 20} fontSize="18" style={{ userSelect: "none" }}>🐻‍❄️</text>}
      {tc.decorations.igloo?.visible !== false && <><ellipse cx={VB.w * 0.05 + 15} cy={groundY + 3} rx="15" ry="12" fill="#E0F2FE" /><rect x={VB.w * 0.05 + 10} y={groundY - 3} width="10" height="10" rx="4" fill="#BAE6FD" /></>}
      <polyline points={roadPoints} fill="none" stroke={tc.road.fill} strokeWidth={tc.road.width} strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={roadPoints} fill="none" stroke={tc.road.stroke} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={tc.road.dashArray} />
      {BUILDINGS.map((b, i) => <Building key={i} b={b} stop={stops[i]} idx={i} color={colorOf(i)} emoji={emojiOf(i)} isSel={selected === i} isCur={i === currentIdx} label={stops[i].game?.name?.split(" ").slice(0, 2).join(" ") || BUILDING_LABELS[i]} onSelect={() => setSelected(i)} />)}
      {(() => { const cb = BUILDINGS[mascotAt] || BUILDINGS[0]; return (<foreignObject x={cb.x + cb.w / 2 - 14} y={cb.y - cb.h - 34} width="28" height="28" style={{ overflow: "visible", pointerEvents: "none", transition: "x 0.4s ease, y 0.4s ease" }}><div style={{ animation: "lwBob 2.4s ease-in-out infinite", filter: "drop-shadow(0 2px 4px rgba(0,0,0,.2))" }}><CabiMascot mood="happy" size={26} /></div></foreignObject>); })()}
      <rect x={isPortrait ? 30 : VB.w * 0.03} y={groundY - 30} width="4" height="45" rx="2" fill="#94A3B8" />
      <rect x={isPortrait ? 14 : VB.w * 0.015} y={groundY - 35} width="55" height="16" rx="4" fill="#3B82F6" />
      <text x={isPortrait ? 41 : VB.w * 0.04} y={groundY - 23} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 9, fill: "#fff" }}>{tc.signpost.label}</text>
      <rect x={isPortrait ? 18 : VB.w * 0.02} y={groundY - 18} width="45" height="12" rx="3" fill="#06B6D4" />
      <text x={isPortrait ? 40 : VB.w * 0.04} y={groundY - 10} textAnchor="middle" style={{ fontFamily: FF, fontWeight: 700, fontSize: 7, fill: "#fff" }}>{tc.signpost.sublabel}</text>
    </svg>
  );
}

function darken(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `rgb(${r},${g},${b})`;
}

function lighten(hex, amount) {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, (n >> 16) + amount);
  const g = Math.min(255, ((n >> 8) & 0xff) + amount);
  const b = Math.min(255, (n & 0xff) + amount);
  return `rgb(${r},${g},${b})`;
}

function mergeDeep(target, source) {
  if (!source) return target;
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])
        && target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
      result[key] = mergeDeep(target[key], source[key]);
    } else if (source[key] !== undefined && source[key] !== null) {
      result[key] = source[key];
    }
  }
  return result;
}

const SCENE_DEFAULTS = {
  town: {
    sky: { stops: [{ offset: 0, color: "#7EC8E3" }, { offset: 0.35, color: "#B5DFF5" }, { offset: 0.6, color: "#D6EFD8" }, { offset: 1, color: "#82C94E" }] },
    ground: { primary: "#72B847", secondary: "#82C94E", accent: "#6DB84A" },
    road: { fill: "#E8D5A8", stroke: "#D0BD88", width: 30, dashArray: "6 18" },
    decorations: { clouds: { visible: true }, sun: { visible: true }, mountains: { visible: true }, trees: { visible: true }, flowers: { visible: true }, pond: { visible: true }, ferrisWheel: { visible: true }, hotAirBalloon: { visible: true }, clockTower: { visible: true }, lamps: { visible: true }, benches: { visible: true }, cars: { visible: true }, playground: { visible: true }, flag: { visible: true } },
    signpost: { label: "Cabil Town", sublabel: "Learn!" },
  },
  island: {
    sky: { stops: [{ offset: 0, color: "#1CA5C9" }, { offset: 0.4, color: "#67D5E8" }, { offset: 0.65, color: "#A8E6CF" }, { offset: 1, color: "#E8C87A" }] },
    ground: { primary: "#E8C87A", secondary: "#F2D98A", accent: "#6DBF5A" },
    road: { fill: "#F2D98A", stroke: "#E8C87A", width: 28, dashArray: "6 18" },
    decorations: { clouds: { visible: true }, sun: { visible: true }, palmTrees: { visible: true }, treasureChest: { visible: true }, pirateShip: { visible: true } },
    signpost: { label: "Pirate Isle", sublabel: "Explore!" },
  },
  space: {
    sky: { stops: [{ offset: 0, color: "#0B0E2A" }, { offset: 0.4, color: "#1A1E4A" }, { offset: 0.7, color: "#2D2670" }, { offset: 1, color: "#4A3C8A" }] },
    ground: { primary: "#2A2650", secondary: "#5A4FA0", accent: "#3D3780" },
    road: { fill: "#5A4FA0", stroke: "#8A7FE0", width: 28, dashArray: "6 18" },
    decorations: { stars: { visible: true }, planet: { visible: true }, nebula: { visible: true }, rocket: { visible: true }, satellite: { visible: true }, aliens: { visible: true } },
    signpost: { label: "Star Base", sublabel: "Blast off!" },
  },
  jungle: {
    sky: { stops: [{ offset: 0, color: "#4A8B3F" }, { offset: 0.3, color: "#6DBF5A" }, { offset: 0.6, color: "#A8D5A0" }, { offset: 1, color: "#2E7D32" }] },
    ground: { primary: "#2E7D32", secondary: "#388E3C", accent: "#1B5E20" },
    road: { fill: "#8B6B3E", stroke: "#6B4F2B", width: 26, dashArray: "4 12" },
    decorations: { vines: { visible: true }, parrots: { visible: true }, monkeys: { visible: true }, waterfall: { visible: true }, mist: { visible: true } },
    signpost: { label: "Wild Jungle", sublabel: "Discover!" },
  },
  underwater: {
    sky: { stops: [{ offset: 0, color: "#0A3D62" }, { offset: 0.3, color: "#0E6F9E" }, { offset: 0.6, color: "#1CA5C9" }, { offset: 1, color: "#0E4D64" }] },
    ground: { primary: "#0E4D64", secondary: "#1A6B5A", accent: "#0A3D48" },
    road: { fill: "#1CA5C9", stroke: "#0891B2", width: 26, dashArray: "6 14" },
    decorations: { bubbles: { visible: true }, lightRays: { visible: true }, coral: { visible: true }, seaweed: { visible: true }, fish: { visible: true }, whale: { visible: true }, treasure: { visible: true } },
    signpost: { label: "Deep Ocean", sublabel: "Dive in!" },
  },
  candy: {
    sky: { stops: [{ offset: 0, color: "#FFB6C1" }, { offset: 0.3, color: "#FFDAB9" }, { offset: 0.6, color: "#FFE4E1" }, { offset: 1, color: "#FFD1DC" }] },
    ground: { primary: "#FFD1DC", secondary: "#FFB6C1", accent: "#FFDAB9" },
    road: { fill: "#FF69B4", stroke: "#FF1493", width: 28, dashArray: "8 12" },
    decorations: { clouds: { visible: true }, rainbow: { visible: true }, lollipops: { visible: true }, candyCanes: { visible: true }, gummies: { visible: true } },
    signpost: { label: "Candy Land", sublabel: "Sweet!" },
  },
  arctic: {
    sky: { stops: [{ offset: 0, color: "#1E3A5F" }, { offset: 0.3, color: "#4A90D9" }, { offset: 0.6, color: "#87CEEB" }, { offset: 1, color: "#E0F2FE" }] },
    ground: { primary: "#E0F2FE", secondary: "#BAE6FD", accent: "#D6EFF5" },
    road: { fill: "#BAE6FD", stroke: "#7DD3FC", width: 28, dashArray: "6 18" },
    decorations: { snowflakes: { visible: true }, aurora: { visible: true }, icebergs: { visible: true }, penguins: { visible: true }, polarbear: { visible: true }, igloo: { visible: true } },
    signpost: { label: "Arctic Base", sublabel: "Explore!" },
  },
};

export { SCENE_DEFAULTS, mergeDeep };
